"use client";

import {
  addDays,
  addMonths,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { type KeyboardEvent, useMemo, useRef } from "react";
import { flushSync } from "react-dom";

import { cn } from "@/lib/utils";

import { useCalendarContext } from "../calendar-context";
import {
  type EventIndex,
  isDayFullyDisabled,
  layoutMonthRow,
} from "../event-layout";
import { type CalendarEvent } from "../types";
import {
  capitalizeFirst,
  formatPeriodTitle,
  formatWeekday,
  getDayKey,
  getMonthGrid,
  skipHiddenDays,
} from "../utils";

import { MonthDayCell } from "./month-day-cell";
import { LANE_REM, MonthWeekLanes } from "./month-week-lanes";

// =============================================================================

// Items per cell before "+N more"
const MAX_LANES = 3;
// Room above the events: padding + day number (+ the addon line)
const TOP_REM = 2.125;
const TOP_WITH_ADDON_REM = 3.25;
// Bottom padding of the cells
const BOTTOM_REM = 0.375;

/** Every event (timed or all-day) touching one of the days, once */
function collectEvents(days: Date[], eventIndex: EventIndex): CalendarEvent[] {
  const byId = new Map<string, CalendarEvent>();
  for (const day of days) {
    const key = getDayKey(day);
    for (const event of eventIndex.allDayByDay.get(key) ?? []) {
      byId.set(event.id, event);
    }
    for (const event of eventIndex.timedByDay.get(key) ?? []) {
      byId.set(event.id, event);
    }
  }
  return [...byId.values()];
}

/**
 * Month grid (ARIA grid): weekday labels, then one row per week with its day
 * cells and the event overlay. Arrow keys move the focused date, Enter opens
 * the day. Grows with its content.
 */
export function MonthView({ eventIndex }: { eventIndex: EventIndex }) {
  const {
    date,
    setDate,
    locale,
    weekStartsOn,
    hiddenWeekdays,
    businessHours,
    disabledRanges,
    onDateClick,
    renderDayAddon,
    isDayDisabled,
  } = useCalendarContext();

  const monthStart = startOfMonth(date).getTime();
  const grid = useMemo(
    () => getMonthGrid(new Date(monthStart), weekStartsOn, hiddenWeekdays),
    [monthStart, weekStartsOn, hiddenWeekdays]
  );

  const windowMinutes = businessHours
    ? (businessHours.endHour - businessHours.startHour) * 60
    : 24 * 60;
  const rows = useMemo(
    () =>
      grid.map((week) =>
        layoutMonthRow(
          week,
          collectEvents(week, eventIndex),
          MAX_LANES,
          windowMinutes
        )
      ),
    [grid, eventIndex, windowMinutes]
  );

  const isDisabled = (day: Date) =>
    !!isDayDisabled?.(day) || isDayFullyDisabled(day, disabledRanges);

  // Single tab stop: the focused date, or the first visible day of the month
  // when it falls on a hidden weekday
  const days = grid.flat();
  const wantedKey = getDayKey(skipHiddenDays(date, 1, hiddenWeekdays));
  const focusKey = days.some((day) => getDayKey(day) === wantedKey)
    ? wantedKey
    : getDayKey(
        days.find((day) => isSameMonth(day, new Date(monthStart))) ?? days[0]
      );

  const gridRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Keys typed in a portaled popover are not for the grid
    if (!event.currentTarget.contains(event.target as Node)) return;
    const cell = (event.target as HTMLElement).closest<HTMLElement>(
      '[role="gridcell"]'
    );
    const day = days.find((d) => getDayKey(d) === cell?.dataset.day);
    if (!day) return;

    let next: Date;
    switch (event.key) {
      case "ArrowLeft":
        next = skipHiddenDays(addDays(day, -1), -1, hiddenWeekdays);
        break;
      case "ArrowRight":
        next = skipHiddenDays(addDays(day, 1), 1, hiddenWeekdays);
        break;
      case "ArrowUp":
        next = addDays(day, -7);
        break;
      case "ArrowDown":
        next = addDays(day, 7);
        break;
      case "Home":
        next = skipHiddenDays(
          startOfWeek(day, { weekStartsOn }),
          1,
          hiddenWeekdays
        );
        break;
      case "End":
        next = skipHiddenDays(
          startOfDay(endOfWeek(day, { weekStartsOn })),
          -1,
          hiddenWeekdays
        );
        break;
      case "PageUp":
      case "PageDown":
        next = skipHiddenDays(
          addMonths(day, event.key === "PageUp" ? -1 : 1),
          1,
          hiddenWeekdays
        );
        break;
      case "Enter":
      case " ":
        if (onDateClick && !isDisabled(day)) {
          event.preventDefault();
          onDateClick(day);
        }
        return;
      default:
        return;
    }
    event.preventDefault();
    // Render the new focused date now, then move the focus onto its cell
    // (another month may have replaced every cell)
    flushSync(() => setDate(next));
    gridRef.current
      ?.querySelector<HTMLElement>(
        `[role="gridcell"][data-day="${getDayKey(next)}"]`
      )
      ?.focus();
  };

  const topRem = renderDayAddon ? TOP_WITH_ADDON_REM : TOP_REM;
  const gridTemplateColumns = `repeat(${grid[0].length}, minmax(0, 1fr))`;

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={formatPeriodTitle(
        "month",
        date,
        locale,
        weekStartsOn,
        hiddenWeekdays
      )}
      onKeyDown={handleKeyDown}
      className="select-none"
    >
      <div
        role="row"
        className="grid border-b border-line"
        style={{ gridTemplateColumns }}
      >
        {grid[0].map((day) => (
          <div
            key={day.getDay()}
            role="columnheader"
            aria-label={capitalizeFirst(format(day, "EEEE", { locale }))}
            className="py-2 text-center text-xs font-medium text-muted-foreground"
          >
            <span aria-hidden className="sm:hidden">
              {capitalizeFirst(format(day, "EEEEEE", { locale }))}
            </span>
            <span aria-hidden className="max-sm:hidden">
              {formatWeekday(day, locale)}
            </span>
          </div>
        ))}
      </div>

      {grid.map((week, rowIndex) => {
        const layout = rows[rowIndex];
        const lanes = Math.max(MAX_LANES, layout.laneCount);
        return (
          <div
            key={week[0].getTime()}
            role="row"
            className="relative grid"
            style={{
              gridTemplateColumns,
              minHeight: `${topRem + lanes * LANE_REM + BOTTOM_REM}rem`,
            }}
          >
            {week.map((day, colIndex) => {
              const dayKey = getDayKey(day);
              return (
                <MonthDayCell
                  key={dayKey}
                  day={day}
                  dayKey={dayKey}
                  isToday={isToday(day)}
                  isOutside={!isSameMonth(day, new Date(monthStart))}
                  isDisabled={isDisabled(day)}
                  isFocusTarget={dayKey === focusKey}
                  className={cn(
                    colIndex > 0 && "border-l border-line",
                    rowIndex > 0 && "border-t border-line"
                  )}
                />
              );
            })}
            <MonthWeekLanes
              week={week}
              layout={layout}
              topRem={topRem}
              windowMinutes={windowMinutes}
            />
          </div>
        );
      })}
    </div>
  );
}
